import React from "react";

/**
 * WatermarkedImage — wraps an <img> with a tiled diagonal watermark
 * overlay carrying the architect's firm/name to deter casual copying
 * from the public marketplace.
 *
 * The watermark is rendered via a CSS background pattern that repeats
 * the label every ~180px on a 25° diagonal, in low-opacity indigo. It
 * sits above the image but below any other UI in normal flow.
 *
 * The overlay is screenshotable (it IS the visible image), which is
 * the point — anyone capturing the drawing carries the watermark with
 * them. Sophisticated removal is possible (this is PoC-grade) but the
 * intent is to make casual reuse obviously attributable.
 */
export default function WatermarkedImage({
  src,
  alt,
  watermark,
  testid,
  imgTestid,
  rounded = 4,
  style = {},
  imgStyle = {},
}) {
  const label = (watermark || "").trim() || "WHOLE Neighborhoods";
  return (
    <div
      data-testid={testid}
      style={{
        position: "relative",
        display: "block",
        overflow: "hidden",
        borderRadius: rounded,
        ...style,
      }}
    >
      <img
        src={src}
        alt={alt}
        data-testid={imgTestid}
        style={{
          display: "block",
          width: "100%",
          ...imgStyle,
        }}
      />
      {/* Diagonal repeating watermark layer */}
      <div
        aria-hidden
        data-testid={testid ? `${testid}-watermark` : undefined}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          // SVG data URL repeated as a background — tiled, rotated 25°.
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,"
            + "<svg xmlns='http://www.w3.org/2000/svg' "
            + "width='420' height='180' viewBox='0 0 420 180'>"
            + "<g transform='rotate(-25 210 90)' "
            + "fill='%231B1F4B' fill-opacity='0.14' "
            + "font-family='Helvetica, Arial, sans-serif' "
            + "font-size='17' font-weight='700' "
            + "letter-spacing='0.18em' "
            + "text-anchor='middle'>"
            + `<text x='210' y='98'>${escapeXml(label).toUpperCase()}</text>`
            + "</g></svg>\")",
          backgroundRepeat: "repeat",
          backgroundSize: "420px 180px",
          mixBlendMode: "multiply",
        }}
      />
    </div>
  );
}

function escapeXml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
